<div class="timeline-container">
  <!-- Header -->
  <div class="timeline-header">
    <span class="timeline-title">Timeline</span>

    <div class="timeline-zoom">
      <button class="zoom-btn" (click)="zoomOut()" title="Zoom out">−</button>
      <span class="zoom-label">{{ pixelsPerSecond() }}px/s</span>
      <button class="zoom-btn" (click)="zoomIn()" title="Zoom in">+</button>
    </div>

    <div class="timeline-readout" title="Frame within the scene under the playhead / that scene's framesCount, then the frame within the chapter (the number the player itself holds).">
      <span class="readout-time">{{ timecode() }}</span>
      @if (playhead().block; as block) {
        <span class="readout-scene">f{{ playhead().sceneFrame }}/{{ block.framesCount }}</span>
      }
      <span class="readout-frame">ch f{{ playhead().chapterFrame }}</span>
    </div>

    <span class="timeline-duration">{{ totalDurationSeconds().toFixed(1) }}s</span>
  </div>

  <!-- Scrollable content -->
  @if (scenesLoading()) {
    <div class="timeline-loading">Loading scenes...</div>
  } @else {
    <div class="timeline-tracks" cdkScrollable>
      <div class="timeline-grid" [style.width.px]="timelineWidthPx() + 50">
        @if (sceneBlocks().length) {
          <!-- Playhead -->
          @if (currentTimeMs() >= 0) {
            <div class="playhead" [style.left.px]="playheadPx()"></div>
          }
          <!-- Ruler row (click/drag to seek) -->
          <div class="timeline-track-row timeline-track-row--ruler">
            <div class="timeline-row-label timeline-row-label--ruler"></div>
            <div class="timeline-ruler" (mousedown)="onRulerMouseDown($event)">
              @for (tick of rulerTicks(); track tick.seconds) {
                <div
                  class="ruler-tick"
                  [class.ruler-tick--major]="tick.major"
                  [style.left.px]="tick.px"
                >
                  <div class="ruler-tick-line"></div>
                  @if (tick.major) {
                    <span class="ruler-tick-label">{{ tick.label }}</span>
                  }
                </div>
              }
            </div>
          </div>
        }

        <!-- Video row (always visible for drop) -->
        <div class="timeline-track-row">
          <div class="timeline-row-label">Video</div>
          <div
            class="timeline-row"
            [class.timeline-row--dragover]="videoDragOver()"
            [class.timeline-row--empty]="!sceneBlocks().length"
            cdkDropList
            cdkDropListOrientation="horizontal"
            [cdkDropListData]="sceneBlocks()"
            [cdkDropListDisabled]="isReadOnly()"
            (cdkDropListDropped)="dropScene($event)"
            (dragover)="onVideoDragOver($event)"
            (dragleave)="onVideoDragLeave()"
            (drop)="onVideoDrop($event)"
          >
            @for (block of sceneBlocks(); track $index) {
              <div
                class="timeline-block"
                [class.timeline-block--active]="block.slug === activeSceneSlug()"
                [class.timeline-block--disabled]="block.disabled"
                [class.timeline-block--ungenerated]="block.hasVersions === false"
                [class.timeline-block--split]="block.splitParent"
                [class.timeline-block--split-first]="block.splitIndex === 0"
                [class.timeline-block--split-last]="block.splitParent && block.splitIndex === (block.split ?? 1) - 1"
                [style.width.px]="block.durationSeconds * pixelsPerSecond()"
                (click)="clickScene(block)"
                cdkDrag
                [cdkDragDisabled]="!!block.splitParent || isReadOnly()"
              >
                @if (block.isImage) {
                  <i class="pi pi-image timeline-block-icon" title="Image scene"></i>
                }
                <span class="timeline-block-label">{{ block.slug }}</span>
                <span class="timeline-block-duration">{{ block.durationSeconds.toFixed(1) }}s</span>
                @if (!isReadOnly()) {
                  <button class="timeline-block-remove" (click)="removeScene(block, $event)" title="Remove scene">
                    <i class="pi pi-times" style="font-size: 7px;"></i>
                  </button>
                }
              </div>
            }
            @if (!sceneBlocks().length) {
              <span class="drop-hint">Drop video or image here</span>
            }
            @if (insertIndicatorPx() !== null) {
              <div class="drop-insert-indicator" [style.left.px]="insertIndicatorPx()"></div>
            }
          </div>
        </div>

        <!-- Audio rows (one per track) -->
        @for (block of audioBlocks(); track $index) {
          <div class="timeline-track-row">
            <div class="timeline-row-label">Audio {{ $index + 1 }}</div>
            <div class="timeline-audio-row">
              <div
                class="timeline-audio-block"
                [class.timeline-audio-block--active]="block.slug === activeAudioSlug()"
                [class.timeline-audio-block--disabled]="block.disabled"
                [class.timeline-audio-block--ungenerated]="block.hasVersions === false"
                [style.left.px]="block.startSeconds * pixelsPerSecond()"
                [style.width.px]="block.durationSeconds * pixelsPerSecond()"
                (click)="clickAudio(block)"
              >
                <span class="timeline-audio-block-label">{{ block.slug }}</span>
                @if (!isReadOnly()) {
                  <button class="timeline-audio-block-remove" (click)="removeAudio(block, $event)" title="Remove audio">
                    <i class="pi pi-times" style="font-size: 7px;"></i>
                  </button>
                }
              </div>
            </div>
          </div>
        }

        <!-- Audio drop zone — hidden on imports so users can't see an affordance that won't respond -->
        @if (!isReadOnly()) {
          <div
            class="timeline-track-row timeline-track-row--drop"
            [class.timeline-track-row--dragover]="audioDragOver()"
            (dragover)="onAudioDragOver($event)"
            (dragleave)="onAudioDragLeave()"
            (drop)="onAudioDrop($event)"
          >
            <div class="timeline-row-label">Audio</div>
            <div class="timeline-audio-row timeline-audio-row--drop">
              <span class="drop-hint">Drop audio here</span>
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>
